import React from 'react';
import { HeaderConfig } from './HeaderConfig/HeaderConfig';
import { FooterConfig } from './FooterConfig/FooterConfig';
import { PagesConfig } from './PagesConfig/PagesConfig';
import { GroupConfig } from './GroupConfig/GroupConfig';
import { PageConfig } from './PageConfig/PageConfig';
import { ContainerConfig } from './ContainerConfig/ContainerConfig';
import { SelectedElementType } from '../../pages/Editor/ModelEditor';
import { FieldConfigPanel } from './FieldConfig/FieldConfigPanel';
import { useComponentRegistry } from '../../contexts/ComponentRegistryContext';
import { SubmodelConfig } from './SubmodelConfig/SubmodelConfig';
import { CategoryConfig } from './CategoryConfig/CategoryConfig';
import YourBuildWidgetConfigPanel from './WidgetConfig/YourBuildWidgetConfig';
import ButtonWidgetConfigPanel from './WidgetConfig/ButtonWidgetConfig';

type ConfigPanelProps = {
    id?: string;
    projectId: string;
    selectedElement?: SelectedElementType;
    config?: any;
    onConfigChange?: (config: any) => void;
}

export const ConfigPanel = ({ id, projectId, selectedElement, config, onConfigChange }: ConfigPanelProps) => {
    const baseId = id || 'config-panel';
    const { getComponentDefinition } = useComponentRegistry();

    const renderConfigPanel = () => {
        if (!selectedElement) {
            return (
                <div id={`${baseId}-empty-state`} className="tw-p-4 tw-text-gray-500 tw-text-center">
                    Select an element to configure
                </div>
            );
        }

        switch (selectedElement.type) {
            case 'header':
                return (
                    <HeaderConfig
                        id={`${baseId}-header-config`}
                        initialConfig={config}
                        onConfigChange={(updatedConfig) => onConfigChange?.({
                            ...updatedConfig,
                            id: selectedElement.id
                        })}
                    />
                );

            case 'footer':
                return (
                    <FooterConfig
                        id={`${baseId}-footer-config`}
                        initialConfig={config}
                        onConfigChange={(updatedConfig) => onConfigChange?.({
                            ...updatedConfig,
                            id: selectedElement.id
                        })}
                    />
                );

            case 'navigation':
                return (
                    <PagesConfig
                        id={`${baseId}-pages-config`}
                        initialConfig={config}
                        onConfigChange={(updatedConfig) => onConfigChange?.({
                            ...updatedConfig,
                            id: selectedElement.id
                        })}
                    />
                );

            case 'group':
                return (
                    <GroupConfig
                        id={`${baseId}-group-config`}
                        initialConfig={config}
                        onConfigChange={(updatedConfig) => onConfigChange?.({
                            ...updatedConfig,
                            id: selectedElement.id
                        })}
                    />
                );

            case 'page':
                return (
                    <PageConfig
                        id={`${baseId}-page-config`}
                        initialConfig={config}
                        onConfigChange={(updatedConfig) => onConfigChange?.({
                            ...updatedConfig,
                            id: selectedElement.id
                        })}
                    />
                );
            case 'submodel':
                const submodelInfo = getComponentDefinition(selectedElement.id);
                return (
                    <SubmodelConfig
                        id={`${baseId}-submodel-config`}
                        submodelId={selectedElement.id}
                        submodelName={submodelInfo?.name || 'Submodel'}
                        onConfigChange={(updatedConfig) => onConfigChange?.({
                            ...updatedConfig,
                            id: selectedElement.id
                        })}
                    />
                );


            case 'container':
                return (
                    <ContainerConfig
                        id={`${baseId}-container-config`}
                        containerId={selectedElement?.id}
                        key={selectedElement?.id}
                    />
                );

                
                case 'field':
                    const fieldDef = getComponentDefinition(selectedElement.id);
                    const fieldType = selectedElement.fieldType || (fieldDef?.type === 'category' ? 'category' : 'field');

                    if (fieldType === 'category' || fieldDef?.type === 'category') {
                        return (
                            <CategoryConfig
                                id={`${baseId}-category-config`}
                                projectId={projectId}
                                categoryId={selectedElement.id}
                                categoryName={fieldDef?.name || 'Category'}
                                onConfigChange={(updatedConfig: any) => onConfigChange?.({
                                    ...updatedConfig,
                                    id: selectedElement.id
                                })}
                            />
                        );
                    }

                    return (
                        <FieldConfigPanel
                        id={`${baseId}-field-config`}
                        fieldId={selectedElement.id}
                        fieldType={fieldType}
                        parentId={selectedElement.parentId}
                        config={config}
                        onConfigChange={(updatedConfig: any) => onConfigChange?.({
                            ...updatedConfig,
                            id: selectedElement.id
                        })}
                        />
                    );

            case 'widget':
                // Check if it's the Your Build widget
                if (selectedElement.widgetType === 'yourBuild') {
                    return (
                        <YourBuildWidgetConfigPanel
                            id={`${baseId}-widget-config`}
                            config={config || {
                                showTitle: true,
                                title: 'Your Build',
                                showShareButton: true,
                                shareButtonConfig: {
                                    showText: true,
                                    buttonLabel: 'Share',
                                    showIcon: false
                                },
                                showDescription: true,
                                description: 'Disclaimer: Compatio provides product configurations and recommendations as a guide.',
                                showAddToCartButton: true,
                                addToCartButtonConfig: {
                                    showText: true,
                                    buttonLabel: 'Add to Cart',
                                    showIcon: false
                                }
                            }}
                            onConfigChange={(updatedConfig) => onConfigChange?.({
                                ...updatedConfig,
                                id: selectedElement.id
                            })}
                        />
                    );
                }
                // Check if it's the Button widget
                if (selectedElement.widgetType === 'button') {
                    return (
                        <ButtonWidgetConfigPanel
                            id={`${baseId}-widget-config`}
                            config={config || {
                                displayName: 'Button',
                                className: '',
                                functionName: ''
                            }}
                            onConfigChange={(updatedConfig) => onConfigChange?.({
                                ...updatedConfig,
                                id: selectedElement.id
                            })}
                        />
                    );
                }
                return (
                    <div id={`${baseId}-unknown-widget`} className="tw-p-4 tw-text-gray-500 tw-text-center">
                        Widget configuration for {selectedElement.widgetType || 'unknown widget'}
                    </div>
                );

            default:
                return (
                    <div id={`${baseId}-unknown-type`} className="tw-p-4 tw-text-gray-500 tw-text-center">
                        Unknown element type
                    </div>
                );
        }
    };

    return (
        <div id={baseId} data-testid={baseId} className="tw-w-auto tw-bg-white tw-overflow-y-auto">
            {renderConfigPanel()}
        </div>
    );
};